"use client";

import React, { useState } from "react";
import { useToast } from "./Toast";

interface LandingPageGeneratorProps {
  className?: string;
}

export default function LandingPageGenerator({ className = "" }: LandingPageGeneratorProps) {
  const [product, setProduct] = useState("");
  const [offer, setOffer] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [pageType, setPageType] = useState("VENDAS");
  const [conversionGoal, setConversionGoal] = useState("VENDA");
  const [brandColors, setBrandColors] = useState("AZUL E LARANJA");
  const [landingPage, setLandingPage] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { addToast } = useToast();

  const generateLandingPage = async () => {
    if (!product || !offer || !targetAudience) {
      addToast("⚠️ Preencha todos os campos obrigatórios", "error");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch("/api/landing-page", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          product, 
          offer, 
          targetAudience, 
          pageType, 
          conversionGoal, 
          brandColors 
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setLandingPage(data);
        addToast("🌐 Landing page gerada com sucesso!", "success");
      } else {
        const error = await response.json();
        addToast(error.error || "Erro ao gerar landing page", "error");
      }
    } catch (error) {
      console.error("Erro:", error);
      addToast("Erro ao gerar landing page", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const copySection = (content: string) => {
    navigator.clipboard.writeText(content);
    addToast("📋 Seção copiada!", "success");
  };

  return (
    <div className={`bg-gradient-to-br from-teal-900 via-blue-900 to-indigo-900 text-white rounded-lg p-6 border border-teal-500/30 font-mono ${className}`}>
      <div className="mb-6">
        <h3 className="text-2xl font-bold mb-2 text-teal-300">
          🌐 LANDING PAGE GENERATOR 🌐
        </h3>
        <div className="text-xs text-teal-400 mb-4">
          [LANDING_BUILDER] v2.0.0 | STATUS: ACTIVE | AUTH: CONVERSION_MASTER
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-xs text-teal-400 mb-2">PRODUCT_NAME:</label>
          <input
            type="text"
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            className="w-full bg-teal-950/50 border border-teal-500/50 text-teal-300 px-3 py-2 rounded focus:outline-none focus:border-teal-400 focus:shadow-[0_0_10px_rgba(20,184,166,0.3)]"
            placeholder="ex: Curso de Marketing Digital"
          />
        </div>

        <div>
          <label className="block text-xs text-teal-400 mb-2">OFFER_DESCRIPTION:</label>
          <input
            type="text"
            value={offer}
            onChange={(e) => setOffer(e.target.value)}
            className="w-full bg-teal-950/50 border border-teal-500/50 text-teal-300 px-3 py-2 rounded focus:outline-none focus:border-teal-400"
            placeholder="ex: Curso completo com 80% OFF"
          />
        </div>

        <div>
          <label className="block text-xs text-teal-400 mb-2">TARGET_AUDIENCE:</label>
          <input
            type="text"
            value={targetAudience}
            onChange={(e) => setTargetAudience(e.target.value)}
            className="w-full bg-teal-950/50 border border-teal-500/50 text-teal-300 px-3 py-2 rounded focus:outline-none focus:border-teal-400"
            placeholder="ex: Empreendedores iniciantes"
          />
        </div>

        <div>
          <label className="block text-xs text-teal-400 mb-2">PAGE_TYPE:</label>
          <select
            value={pageType}
            onChange={(e) => setPageType(e.target.value)}
            className="w-full bg-teal-950/50 border border-teal-500/50 text-teal-300 px-3 py-2 rounded focus:outline-none focus:border-teal-400"
          >
            <option value="VENDAS">SALES_PAGE</option>
            <option value="CAPTURA">LEAD_CAPTURE</option>
            <option value="WEBINAR">WEBINAR_REGISTRATION</option>
            <option value="LANÇAMENTO">PRODUCT_LAUNCH</option>
            <option value="DOWNLOAD">FREE_DOWNLOAD</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-teal-400 mb-2">CONVERSION_GOAL:</label>
          <select
            value={conversionGoal}
            onChange={(e) => setConversionGoal(e.target.value)}
            className="w-full bg-teal-950/50 border border-teal-500/50 text-teal-300 px-3 py-2 rounded focus:outline-none focus:border-teal-400"
          >
            <option value="VENDA">SALE</option>
            <option value="LEAD">LEAD_GENERATION</option>
            <option value="INSCRIÇÃO">REGISTRATION</option>
            <option value="DOWNLOAD">DOWNLOAD</option>
            <option value="CONTATO">CONTACT_REQUEST</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-teal-400 mb-2">BRAND_COLORS:</label>
          <select
            value={brandColors}
            onChange={(e) => setBrandColors(e.target.value)}
            className="w-full bg-teal-950/50 border border-teal-500/50 text-teal-300 px-3 py-2 rounded focus:outline-none focus:border-teal-400"
          >
            <option value="AZUL E LARANJA">BLUE_ORANGE</option>
            <option value="VERDE E BRANCO">GREEN_WHITE</option>
            <option value="PRETO E DOURADO">BLACK_GOLD</option>
            <option value="ROXO E ROSA">PURPLE_PINK</option>
            <option value="VERMELHO E BRANCO">RED_WHITE</option>
          </select>
        </div>
      </div>

      <button
        onClick={generateLandingPage}
        disabled={isLoading || !product || !offer || !targetAudience}
        className="w-full bg-gradient-to-r from-teal-600 to-blue-600 text-white py-3 px-6 rounded font-mono text-sm hover:from-teal-700 hover:to-blue-700 disabled:from-gray-600 disabled:to-gray-600 transition-all duration-300 mb-6"
      >
        {isLoading ? (
          <span className="flex items-center justify-center">
            <span className="animate-pulse">⚡</span> BUILDING_PAGE...
          </span>
        ) : (
          <span className="flex items-center justify-center">
            🌐 [GENERATE_LANDING] 🌐
          </span>
        )}
      </button>

      {landingPage && (
        <div className="mt-6 space-y-4">
          <div className="text-xs text-teal-400 mb-4">
            [LANDING_GENERATED] | CONVERSION: {landingPage.estimated_conversion}% | OPTIMIZED: YES
          </div>
          
          {/* Hero Section */}
          <div className="bg-teal-950/30 border border-teal-500/30 rounded p-4 mb-4">
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-lg font-bold text-teal-300">
                🚀 HERO_SECTION
              </h4>
              <button
                onClick={() => copySection(JSON.stringify(landingPage.hero_section, null, 2))}
                className="text-xs bg-teal-500/20 px-2 py-1 rounded hover:bg-teal-500/30"
              >
                [COPY_SECTION]
              </button>
            </div>
            
            <div className="mb-3">
              <h5 className="text-sm font-bold text-teal-400 mb-2">HEADLINE:</h5>
              <div className="text-teal-300 text-lg font-bold bg-black/30 p-2 rounded">{landingPage.hero_section?.headline}</div>
            </div>

            <div className="mb-3">
              <h5 className="text-sm font-bold text-teal-400 mb-2">SUBHEADLINE:</h5>
              <div className="text-teal-300 text-sm bg-black/30 p-2 rounded">{landingPage.hero_section?.subheadline}</div>
            </div>

            <div className="mb-3">
              <h5 className="text-sm font-bold text-teal-400 mb-2">CTA_BUTTON:</h5>
              <div className="text-teal-300 bg-black/30 p-2 rounded">
                <span className="font-bold">TEXT:</span> {landingPage.hero_section?.cta_button?.text}
                <span className="ml-4 font-bold">COLOR:</span> {landingPage.hero_section?.cta_button?.color}
              </div>
            </div>

            <div className="mb-3">
              <h5 className="text-sm font-bold text-teal-400 mb-2">SOCIAL_PROOF:</h5>
              <div className="text-teal-300 text-sm bg-black/30 p-2 rounded">{landingPage.hero_section?.social_proof?.text}</div>
            </div>
          </div>

          {/* Value Proposition */}
          <div className="bg-teal-950/30 border border-teal-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-teal-300 mb-3">💎 VALUE_PROPOSITION</h4>
            <div className="space-y-2">
              {landingPage.value_proposition?.pillars?.map((pillar: any, index: number) => (
                <div key={index} className="bg-black/30 p-2 rounded">
                  <span className="text-teal-400 font-bold">{pillar.icon}</span>
                  <span className="text-teal-300 ml-2 font-bold">{pillar.title}</span>
                  <div className="text-teal-300 text-sm mt-1">{pillar.description}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Features Section */}
          <div className="bg-teal-950/30 border border-teal-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-teal-300 mb-3">🔧 FEATURES_SECTION</h4>
            <div className="space-y-2">
              {landingPage.features_section?.features?.map((feature: any, index: number) => (
                <div key={index} className="bg-black/30 p-2 rounded">
                  <div className="text-teal-300 font-bold">{feature.title}</div>
                  <div className="text-teal-300 text-sm">{feature.description}</div>
                  <div className="text-teal-400 text-xs mt-1">💡 {feature.benefit}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Social Proof */}
          <div className="bg-teal-950/30 border border-teal-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-teal-300 mb-3">⭐ SOCIAL_PROOF</h4>
            
            <div className="mb-3">
              <h5 className="text-sm font-bold text-teal-400 mb-2">TESTIMONIALS:</h5>
              <div className="space-y-2">
                {landingPage.social_proof?.testimonials?.map((testimonial: any, index: number) => (
                  <div key={index} className="bg-black/30 p-2 rounded">
                    <div className="text-teal-300 italic">"{testimonial.text}"</div>
                    <div className="text-teal-400 text-sm mt-1">- {testimonial.name}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <h5 className="text-sm font-bold text-teal-400 mb-2">STATISTICS:</h5>
              <div className="grid grid-cols-3 gap-2">
                {landingPage.social_proof?.statistics?.map((stat: any, index: number) => (
                  <div key={index} className="bg-black/30 p-2 rounded text-center">
                    <div className="text-teal-300 font-bold text-lg">{stat.number}</div>
                    <div className="text-teal-400 text-xs">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Offer Section */}
          <div className="bg-teal-950/30 border border-teal-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-teal-300 mb-3">💰 OFFER_SECTION</h4>
            
            <div className="mb-3">
              <h5 className="text-sm font-bold text-teal-400 mb-2">PRICING:</h5>
              <div className="bg-black/30 p-2 rounded">
                <div className="text-teal-300">
                  <span className="line-through text-gray-400">R$ {landingPage.offer_section?.price?.original}</span>
                  <span className="ml-2 text-2xl font-bold text-green-400">R$ {landingPage.offer_section?.price?.discounted}</span>
                  <span className="ml-2 text-teal-400">{landingPage.offer_section?.price?.discount_percentage}% OFF</span>
                </div>
                <div className="text-teal-300 text-sm mt-1">{landingPage.offer_section?.price?.installments}</div>
              </div>
            </div>

            <div className="mb-3">
              <h5 className="text-sm font-bold text-teal-400 mb-2">BONUSES:</h5>
              <div className="space-y-1">
                {landingPage.offer_section?.bonuses?.map((bonus: any, index: number) => (
                  <div key={index} className="bg-black/30 p-2 rounded">
                    <span className="text-teal-400 font-bold">{bonus.icon}</span>
                    <span className="text-teal-300 ml-2">{bonus.name}</span>
                    <div className="text-teal-400 text-xs">Valor: {bonus.value}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <h5 className="text-sm font-bold text-teal-400 mb-2">GUARANTEE:</h5>
              <div className="text-teal-300 bg-black/30 p-2 rounded">{landingPage.offer_section?.guarantee?.type} - {landingPage.offer_section?.guarantee?.period}</div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="bg-teal-950/30 border border-teal-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-teal-300 mb-3">❓ FAQ_SECTION</h4>
            <div className="space-y-2">
              {landingPage.faq_section?.questions?.map((qa: any, index: number) => (
                <div key={index} className="bg-black/30 p-2 rounded">
                  <div className="text-teal-300 font-bold">Q: {qa.question}</div>
                  <div className="text-teal-300 text-sm mt-1">A: {qa.answer}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Final CTA */}
          <div className="bg-teal-950/30 border border-teal-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-teal-300 mb-3">🚀 FINAL_CTA</h4>
            <div className="bg-black/30 p-2 rounded">
              <div className="text-teal-300 font-bold text-lg">{landingPage.final_cta?.headline}</div>
              <div className="text-teal-300 text-sm mt-1">{landingPage.final_cta?.subheadline}</div>
              <div className="text-teal-300 mt-2 bg-teal-600/20 p-2 rounded text-center font-bold">
                {landingPage.final_cta?.cta_button?.text}
              </div>
              <div className="text-teal-400 text-xs mt-2 italic">{landingPage.final_cta?.last_chance_message}</div>
            </div>
          </div>

          {/* Technical Specs */}
          <div className="bg-teal-950/20 border border-teal-500/30 rounded p-4">
            <h4 className="text-lg font-bold text-teal-300 mb-3">⚙️ TECHNICAL_SPECS</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              <div className="bg-black/30 p-2 rounded">
                <span className="text-teal-400">RESPONSIVE:</span>
                <span className="text-teal-300 ml-2">✅ YES</span>
              </div>
              <div className="bg-black/30 p-2 rounded">
                <span className="text-teal-400">SEO_OPTIMIZED:</span>
                <span className="text-teal-300 ml-2">✅ YES</span>
              </div>
              <div className="bg-black/30 p-2 rounded">
                <span className="text-teal-400">LOAD_SPEED:</span>
                <span className="text-teal-300 ml-2">OPTIMIZED</span>
              </div>
              <div className="bg-black/30 p-2 rounded">
                <span className="text-teal-400">ANALYTICS:</span>
                <span className="text-teal-300 ml-2">INTEGRATED</span>
              </div>
            </div>
          </div>

          <div className="text-xs text-teal-400 mt-4">
            [LANDING_COMPLETE] | PAGE_TITLE: {landingPage.page_title} | CONVERSION_OPTIMIZED: YES | AUTH: ADMIN
          </div>
        </div>
      )}
    </div>
  );
}
